import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Play, LogOut, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export const Header = ({ embedded = false }) => {
  const [profileOpen, setProfileOpen] = useState(false);
  const { user, loginWithGoogle, logout } = useAuth();

  const handleLogin = async () => {
    try {
      await loginWithGoogle();
    } catch (e) {
      alert('Login failed: ' + (e?.message || 'Please try again'));
    }
  };

  const handleLogout = async () => {
    await logout();
    setProfileOpen(false);
  };

  const UserButton = () => (
    <div className="relative">
      <button
        onClick={() => setProfileOpen(!profileOpen)}
        className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-brand-surface transition-colors"
      >
        {user.photoURL ? (
          <img
            src={user.photoURL}
            alt={user.displayName || 'User'}
            className="w-8 h-8 rounded-full border-2 border-orange-500"
            referrerPolicy="no-referrer"
          />
        ) : (
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center text-white font-bold text-sm">
            {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
          </div>
        )}
        <span className="hidden sm:block text-sm text-white max-w-[120px] truncate">
          {user.displayName || user.email}
        </span>
      </button>

      {profileOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setProfileOpen(false)}
          />
          <div className="absolute right-0 mt-2 w-56 bg-brand-surface border border-brand-surface-3 rounded-xl shadow-2xl z-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-brand-surface-3">
              <p className="text-sm font-medium text-white truncate">
                {user.displayName || 'User'}
              </p>
              <p className="text-xs text-slate-400 truncate">{user.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="w-full px-4 py-3 flex items-center gap-3 text-slate-300 hover:bg-brand-surface-2 hover:text-white transition-colors text-sm"
            >
              <LogOut className="w-4 h-4" />
              Sign out
            </button>
          </div>
        </>
      )}
    </div>
  );

  const LoginButton = () => (
    <button
      onClick={handleLogin}
      className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white text-slate-900 rounded-lg hover:bg-slate-100 transition-all duration-200 font-medium text-sm sm:text-base"
    >
      <svg className="w-4 h-4" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      <span className="hidden sm:inline">Sign in with Google</span>
      <span className="sm:hidden">Sign in</span>
    </button>
  );

  if (embedded) return (
    <header className="relative z-40 bg-brand-bg border-b border-brand-surface">
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-end gap-4">
        <a href={`${window.location.origin}/`} target="_blank" rel="noopener noreferrer"
          className="text-sm text-slate-300 underline">Open full app</a>
        {user ? <UserButton /> : <LoginButton />}
      </div>
    </header>
  );

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-brand-bg/80 backdrop-blur-lg border-b border-brand-surface">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-14 sm:h-16">
          <Link to="/" className="flex items-center gap-2">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="ValueBell Logo"
              className="w-8 h-8 sm:w-10 sm:h-10 object-contain"
            />
            <span className="text-lg sm:text-xl font-bold text-white">VALUEBOT</span>
          </Link>

          <div className="flex items-center gap-3">
            <a
              href="https://www.valuebell.studio/contact"
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-orange-500 to-red-500 rounded-lg hover:from-orange-600 hover:to-red-600 transition-all duration-200"
            >
              Contact Us
            </a>
            {user ? <UserButton /> : <LoginButton />}
          </div>
        </div>
      </div>
    </header>
  );
};
